import { Component } from "react";

/**
 * Lightweight React error boundary.
 * Catches render/lifecycle errors in the child tree and shows a minimal fallback
 * instead of unmounting the entire application.
 *
 * Note: Errors thrown inside async callbacks / most useEffect bodies are not
 * caught by React error boundaries (React limitation). GSAP setup already uses
 * try/catch where plugins are optional (SplitText).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback({ retry: this.handleRetry });
      }
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <DefaultFallback onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ onRetry }) {
  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center bg-[#050505] px-6 text-white"
    >
      <div className="max-w-md text-center">
        <p
          className="font-display text-2xl tracking-tight text-white sm:text-3xl"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Something went wrong.
        </p>
        <p className="mt-4 font-body text-sm leading-relaxed text-white/70">
          This page hit an unexpected error. You can try again without leaving the site.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="cta-solid mt-8 inline-flex rounded-full bg-white px-7 py-3 text-sm font-medium text-black"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/** Compact inline fallback for a single page section — keeps surrounding layout intact. */
export function SectionFallback({ retry }) {
  return (
    <div
      role="alert"
      className="mx-auto max-w-[1400px] border-y border-white/10 px-6 py-16 md:px-10"
    >
      <p className="font-body text-sm text-white/70">This section couldn’t load.</p>
      <button
        type="button"
        onClick={retry}
        className="mt-4 text-sm text-white/70 underline underline-offset-4 transition-colors hover:text-gold"
      >
        Try again
      </button>
    </div>
  );
}
